// 상세 화면의 라벨/값 그리드.
// FieldGrid = 반응형 N열 컨테이너, FieldItem = 단일 라벨/값.
// 빈 값(null / undefined / "" / 공백)은 화면에 절대 "null"·"undefined"로 노출되지 않고
// emptyText("—")로 대체된다. value가 ReactNode(Chip/Link 등)면 그대로 렌더한다.

import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { SxProps, Theme } from "@mui/material/styles";

export interface FieldGridProps {
  columns?: 1 | 2 | 3;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
  className?: string;
}

export const FieldGrid: React.FC<FieldGridProps> = ({
  columns = 2,
  children,
  sx,
  className,
}) => (
  <Box
    className={className}
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        sm: `repeat(${columns}, minmax(0, 1fr))`,
      },
      columnGap: 3,
      rowGap: { xs: 1.5, sm: 1.75 },
      ...sx,
    }}
  >
    {children}
  </Box>
);

export interface FieldItemProps {
  label: string;
  value?: React.ReactNode;
  span?: 1 | 2 | 3;
  emptyText?: string;
  align?: "start" | "end";
  multiline?: boolean;
  sx?: SxProps<Theme>;
  className?: string;
}

const isEmptyValue = (value: React.ReactNode): boolean =>
  value === null ||
  value === undefined ||
  value === "" ||
  typeof value === "boolean" ||
  (typeof value === "string" && value.trim() === "") ||
  (typeof value === "number" && Number.isNaN(value));

export const FieldItem: React.FC<FieldItemProps> = ({
  label,
  value,
  span = 1,
  emptyText = "—",
  align = "start",
  multiline = false,
  sx,
  className,
}) => {
  const empty = isEmptyValue(value);
  const isPrimitive = typeof value === "string" || typeof value === "number";
  // 값만 우측 정렬한다(라벨은 항상 좌측).
  const valueAlign = align === "end" ? "right" : undefined;

  return (
    <Box
      className={className}
      sx={{
        gridColumn: { sm: `span ${span}` },
        ...sx,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mb: 0.25,
          fontWeight: 600,
          color: "text.secondary",
        }}
      >
        {label}
      </Typography>

      {empty ? (
        <Typography
          variant="body2"
          sx={{ color: "text.disabled", textAlign: valueAlign }}
        >
          {emptyText}
        </Typography>
      ) : isPrimitive ? (
        <Typography
          variant="body2"
          sx={{
            color: "text.primary",
            textAlign: valueAlign,
            whiteSpace: multiline ? "pre-wrap" : "normal",
            wordBreak: "break-word",
            fontVariantNumeric: align === "end" ? "tabular-nums" : undefined,
          }}
        >
          {value}
        </Typography>
      ) : (
        value
      )}
    </Box>
  );
};
